import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveMatch, type ActiveMatchDescriptor } from '@/store/activeMatch.slice';
import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';
import { intestazioneValida, ritornoValido, type IntestazioneTorneo } from '@/lib/limulo/ritorno';

type StartDescriptor = Omit<ActiveMatchDescriptor, 'startedAt'> & {
  /**
   * Dove porta «Torna al torneo» a fine partita (`lib/limulo/ritorno.ts`, riga 12 del censimento).
   * ⚠️ NON va nello slice: viaggia nello state della NAVIGAZIONE, e solo se passa `ritornoValido`.
   */
  ritorno?: string | null;
  /**
   * Torneo e round per la riga in cima alla partita (L-2621 a). Stessa strada del ritorno, e SOLO con
   * un ritorno valido: la riga porta li'.
   */
  intestazione?: IntestazioneTorneo | null;
};

/**
 * Start a real-time match: register it as the one active match (so the provider
 * keeps its socket alive) and navigate to its screen. If a DIFFERENT match is
 * already live, confirm first — one match at a time; confirming resigns the old
 * one. `window.confirm` blocks the Telegram WebView briefly but is acceptable
 * here (rare, deliberate action); swap for an in-app sheet later if desired.
 *
 * 🔴 RENDE `true` SE LA PARTITA E' PARTITA, `false` SE L'UTENTE HA RIFIUTATO.
 * Prima rendeva `void` e usciva con un `return` muto: il chiamante non poteva
 * distinguere «sei partito» da «non sei partito», quindi **tutti indovinavano
 * "e' andata"** e lasciavano il proprio stato di avvio acceso per sempre.
 * Misurato su `stg-tma` l'11/09: `POST /matches/practice` riusciva (partita VERA
 * creata sul server), la conferma veniva rifiutata, e il bottone restava su
 * «Avvio…» con la partita appena creata gia' orfana.
 * ⇒ ***Il difetto non era la conferma: era che il «no» non tornava indietro.***
 * ⚠️ Chi chiama e tiene uno stato di avvio DEVE guardare questo valore: se e'
 * `false` non ci sara' nessuna navigazione a rimettere a posto la schermata.
 */
export function useStartMatch() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('activeMatch');
  const { t: tBackgammon } = useTranslation('backgammon');
  const current = useAppSelector((s) => s.activeMatch.current);
  const { resignActive, resaSenzaNumeri } = useActiveMatchContext();

  return useCallback(
    ({ ritorno, intestazione, ...d }: StartDescriptor): boolean => {
      const path =
        d.game === 'qgammon'
          ? `/backgammon/match/${d.matchId}`
          : `/chinesepoker/match/${d.matchId}`;

      if (current && current.matchId !== d.matchId) {
        const domanda = t('alreadyInMatch', {
          defaultValue: 'You already have a match in progress — abandon it?',
        });
        // ADR-0234 §4(e): se la partita che si sta per lasciare ha atti in autoplay, la frase «non conterà per te» sta nella STESSA conferma.
        const ok = window.confirm(resaSenzaNumeri ? `${domanda}\n\n${tBackgammon('match.autoplay.resa')}` : domanda);
        if (!ok) return false;
        resignActive();
      }
      dispatch(setActiveMatch({ ...d, startedAt: Date.now() }));
      const verso = ritornoValido(ritorno);
      const riga = verso === null ? null : intestazioneValida(intestazione);
      navigate(
        path,
        verso === null
          ? undefined
          : { state: riga === null ? { ritorno: verso } : { ritorno: verso, intestazione: riga } },
      );
      return true;
    },
    [current, resignActive, resaSenzaNumeri, dispatch, navigate, t, tBackgammon],
  );
}
